<script setup>
  import { Send, ImageOutline, CloseOutline } from "@vicons/ionicons5";
  import { onBeforeUnmount, ref } from "vue";
  import api from "@/lib/api";

  const props = defineProps({
    dmApiParam: {
      type: Object,
      default: () => ({}),
    },
    reloadEvent: { type: Function, required: true },
  });

  const MAX_IMAGE_COUNT = 3;
  const MAX_IMAGE_SIZE = 20 * 1024 * 1024;

  const message = ref("");
  const isSending = ref(false);
  const inputRef = ref(null);
  const attachImages = ref([]); // { file, previewUrl }

  // 파일 선택/드래그 앤 드랍 공통 첨부 처리 (검증 포함)
  const addFiles = (fileList) => {
    if (isSending.value) return;
    const files = Array.from(fileList || []);
    for (const file of files) {
      if (attachImages.value.length >= MAX_IMAGE_COUNT) {
        alert(`이미지는 최대 ${MAX_IMAGE_COUNT}개까지 첨부할 수 있습니다.`);
        break;
      }
      if (!/\.(jpg|jpeg|png|gif)$/i.test(file.name)) {
        alert('이미지 파일(jpg, jpeg, png, gif)만 첨부할 수 있습니다.');
        continue;
      }
      if (file.size > MAX_IMAGE_SIZE) {
        alert('이미지 크기는 20MB 이하만 업로드 가능합니다.');
        continue;
      }
      attachImages.value.push({
        file,
        previewUrl: URL.createObjectURL(file),
      });
    }
  };

  const handleSelectImage = () => {
    if (attachImages.value.length >= MAX_IMAGE_COUNT) {
      alert(`이미지는 최대 ${MAX_IMAGE_COUNT}개까지 첨부할 수 있습니다.`);
      return;
    }

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.jpg,.jpeg,.png,.gif';
    fileInput.multiple = true;
    fileInput.style.display = 'none';

    fileInput.onchange = (e) => {
      addFiles(e.target.files);
    };

    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  };

  const handleRemoveImage = (index) => {
    URL.revokeObjectURL(attachImages.value[index].previewUrl);
    attachImages.value.splice(index, 1);
  };

  const clearAttachImages = () => {
    attachImages.value.forEach((item) => URL.revokeObjectURL(item.previewUrl));
    attachImages.value = [];
  };

  const handleSendMessage = async () => {
    // 텍스트도 이미지도 없거나 전송 중이면 리턴
    if ((!message.value.trim() && attachImages.value.length === 0) || isSending.value) return;

    isSending.value = true;
    try {
      const formData = new FormData();
      Object.entries(props.dmApiParam).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });
      formData.append("newMsg", message.value);
      formData.append("fromMsg", 'tea');
      formData.append("toMsg", 'admin');
      attachImages.value.forEach((item) => {
        formData.append("files", item.file);
      });

      const { data } = await api.post("/dm/tea/insertNewDm", formData);
      if (data.success) {
        message.value = "";
        clearAttachImages();
        props.reloadEvent(null, false);
        // 메시지 전송 후 입력박스에 다시 포커스
        if (inputRef.value) {
          inputRef.value.focus();
        }
      }
    } finally {
      isSending.value = false;
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  onBeforeUnmount(() => {
    clearAttachImages();
  });

  defineExpose({
    addFiles,
  });
</script>

<template>
  <div class="chat-enter-box">
    <div v-if="attachImages.length > 0" class="attach-preview">
      <div
        v-for="(item, index) in attachImages"
        :key="`attach-img-${index}`"
        class="attach-preview-item"
      >
        <img :src="item.previewUrl" alt="첨부 이미지 미리보기" />
        <button
          class="attach-remove-btn"
          :disabled="isSending"
          @click="handleRemoveImage(index)"
        >
          <n-icon :size="12"><CloseOutline /></n-icon>
        </button>
      </div>
    </div>
    <n-input-group>
      <ui-button
        variant="outlined"
        @click="handleSelectImage"
        :disabled="isSending"
        title="이미지 첨부"
      >
        <template #icon>
          <n-icon><ImageOutline /></n-icon>
        </template>
      </ui-button>
      <n-input
        ref="inputRef"
        v-model:value="message"
        @keydown="handleKeyDown"
        placeholder="메시지 입력"
        :disabled="isSending"
      />
      <ui-button @click="handleSendMessage" :loading="isSending">
        <template #icon>
          <n-icon><Send /></n-icon>
        </template> </ui-button
    ></n-input-group>
  </div>
</template>

<style lang="scss" scoped>
  .chat-enter-box {
    border-top: 1px solid var(--smoe-border);
    background: var(--smoe-white);
    display: flex;
    flex-direction: column;
    width: 100%;
    font-size: 0.875rem;
    padding: 0.5rem;
    .attach-preview {
      display: flex;
      gap: 0.5rem;
      padding-bottom: 0.5rem;
      .attach-preview-item {
        position: relative;
        width: 3.5rem;
        height: 3.5rem;
        border: 1px solid var(--smoe-border);
        border-radius: 0.375rem;
        overflow: hidden;
        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .attach-remove-btn {
          position: absolute;
          top: 2px;
          right: 2px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 1rem;
          height: 1rem;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.55);
          color: #fff;
        }
      }
    }
  }
</style>
